// src/pages/ExampleDoorloopjePage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import InfoModal from '../components/InfoModal';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';
import SEO from '../components/SEO';
import { toDescription } from '../utils/seoText';
import { trackEvent } from '../utils/track';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function ExampleDoorloopjePage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [doorloopje, setDoorloopje] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Alleen bij een echte 'bestaat niet' krijgt de pagina noindex. Bij een tijdelijke
  // laadfout niet: Google zou de pagina dan uit de index halen.
  const [notFound, setNotFound] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [contactData, setContactData] = useState(null);


  useEffect(() => {
    const fetchDoorloopje = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const url = `${STRAPI_URL}/api/example-doorloopjes?filters[slug][$eq]=${slug}&populate=steps.image&populate=downloads`;
        const response = await axios.get(url);

        if (response.data?.data?.length > 0) {
          const rawData = response.data.data[0];
          setDoorloopje(rawData);
        } else {
          setError('Voorbeeld doorloopje niet gevonden');
          setNotFound(true);
        }
      } catch (err) {
        console.error('Fout bij het ophalen van voorbeeld doorloopje:', err);
        setError('Dit voorbeeld laden lukte niet. Probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchDoorloopje();
  }, [slug]);

  useEffect(() => {
    // Fetch privacy and contact data for footer
    const fetchFooterData = async () => {
      try {
        const [privacyRes, contactRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
          axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`)
        ]);
        setPrivacyData(privacyRes.data.data);
        setContactData(contactRes.data.data);
      } catch (err) {
        console.error('Fout bij het ophalen van footer data:', err);
      }
    };

    fetchFooterData();
  }, []);

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleRegisterClick = () => {
    setIsRegisterModalOpen(true);
  };

  if (loading) {
    return (
      <div className="page-container seo-page voorbeeld-page">
        <Header
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
        />
        <main>
          <p>Voorbeeld doorloopje wordt geladen...</p>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  if (error || !doorloopje) {
    return (
      <div className="page-container seo-page voorbeeld-page">
        <SEO
          title="Voorbeeld doorloopje niet gevonden | Doorloopjes"
          description="Dit voorbeeld doorloopje bestaat niet of is verplaatst."
          noindex={notFound}
        />
        <Header
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
        />
        <main>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Voorbeeld doorloopje niet gevonden</h1>
            <p>{error || 'Dit voorbeeld bestaat niet of is verplaatst.'}</p>
            <button onClick={() => navigate('/')} className="btn-primary" style={{ marginTop: '1rem' }}>
              Terug naar home
            </button>
          </div>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  const hasSteps = doorloopje.steps && doorloopje.steps.length > 0;
  const hasDownloads = doorloopje.downloads && doorloopje.downloads.length > 0;

  // Get image from first step
  const firstStepImage = doorloopje.steps?.[0]?.image?.url || null;

  // Extract description
  const getDescription = () =>
    toDescription(
      doorloopje.description,
      `Bekijk het voorbeeld van een doorloopje: "${doorloopje.title}".`
    );

  return (
    <div className="page-container seo-page voorbeeld-page">
      <SEO
        title={`${doorloopje.title} | Doorloopjes`}
        description={getDescription()}
        image={firstStepImage}
        url={`/voorbeeld/${slug}`}
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: doorloopje.title,
          description: getDescription(),
          url: `https://doorloopjes.nl/voorbeeld/${slug}`,
          inLanguage: 'nl',
          ...(firstStepImage ? { image: firstStepImage } : {}),
          step: hasSteps ? doorloopje.steps.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.title || `Stap ${index + 1}`,
            ...(step.image?.url ? { image: step.image.url } : {}),
          })) : [],
          publisher: {
            '@type': 'Organization',
            name: 'Doorloopjes',
            url: 'https://doorloopjes.nl',
          },
        }}
      />
      <Header
        onLoginClick={handleLoginClick}
        onRegisterClick={handleRegisterClick}
      />

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            window.location.href = '/dashboard';
          }}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}

      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterModalOpen(false);
            window.location.href = '/dashboard';
          }}
        />
      )}

      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={false}
        showSocial={false}
      />

      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={false}
        showSocial={true}
      />

      <main>
        <div className="doorloopje-detail-container">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronUpIcon className="back-icon" />
            <span>Terug</span>
          </button>

          <div className="doorloopje-header">
            <div className="doorloopje-title-row">
              <h1>{doorloopje.title}</h1>
            </div>

            {(doorloopje.book || doorloopje.page) && (
              <div className="doorloopje-meta">
                {doorloopje.book && <span>Boek {doorloopje.book}</span>}
                {doorloopje.page && <span>Pagina {doorloopje.page}</span>}
              </div>
            )}
          </div>

          {doorloopje.description && Array.isArray(doorloopje.description) && doorloopje.description.length > 0 && (
            <div className="doorloopje-description">
              <div className="description-content expanded">
                <BlocksRenderer content={doorloopje.description} />
              </div>
            </div>
          )}

          {hasSteps && (
            <div className="doorloopje-steps">
              <h2>Stappen</h2>
              <div className="steps-grid">
                {doorloopje.steps.map((step, index) => (
                  <div key={index} className="step-card">
                    {step.image?.url ? (
                      <div className="step-card-image-container">
                        <div
                          className="step-card-image-background"
                          style={{ backgroundImage: `url(${step.image.url})` }}
                        />
                        <img
                          src={step.image.url}
                          alt={step.title || `Stap ${index + 1}`}
                          className="step-card-image-foreground"
                        />
                      </div>
                    ) : (
                      <div className="step-card-image-container">
                        <div className="step-card-image-placeholder" />
                      </div>
                    )}
                    <div className="step-card-content">
                      <div className="step-number">{index + 1}</div>
                      {step.title && <h3>{step.title}</h3>}
                      {step.description && Array.isArray(step.description) && (
                        <div className="step-description">
                          <BlocksRenderer content={step.description} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasDownloads && (
            <div className="doorloopje-downloads">
              <h2>Download</h2>
              <div className="downloads-list">
                {doorloopje.downloads.map((file) => (
                  <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-link"
                    onClick={() =>
                      trackEvent({
                        type: 'pdf_download',
                        path: window.location.pathname,
                        title: document.title,
                        payload: {
                          content_title: `${doorloopje.title} - ${file.name}`,
                          doorloopje_title: doorloopje.title,
                          file_name: file.name,
                          bron: 'voorbeeld-pagina',
                        },
                      })
                    }
                  >
                    {file.caption || file.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="example-cta-section">
            <div className="example-cta-content">
              <h3>Wil je meer doorloopjes bekijken?</h3>
              <p>Maak gratis een account. Dan kun je alle doorloopjes, de assistent en de kennisbank gebruiken.</p>
              <button onClick={handleRegisterClick} className="btn-primary">
                Gratis registreren
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer
        onLoginClick={handleLoginClick}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );
}

export default ExampleDoorloopjePage;
